import { Observable, of } from 'rxjs';
import {
	CommentVoteState,
	ICCCommentBase,
	ICCCommentQuery,
	ICCCommentsCountResponse,
	ICCCommentsResponse,
	ICCCommentsVoteResponse,
	ICCCommentVotePayload,
	ICommentQueryBase,
	ICommentsApi,
	ICommentsApiMock,
	IDeleteCCComment,
	IEditCCCommentPayload,
	ILessonsCommentsCountsResponse,
	ILPComment,
	ILPCommentBase,
	ILPCommentVotePayload,
	IQuizCommentBase,
	IQuizCommentQuery,
	IQuizCommentResponse,
	IQuizCommentsCountResponse,
	IQuizCommentsVoteResponse,
	QuizCommentType,
} from '../private/comments-api/comments-api.interface';
import { AuthLevel, Authorize } from '../private/authorize.decorator';
import { constructUrl } from '../private/utils/httpParamsUtils';
import { IApiUrls, IHostsConfigs } from '../private/global.interface';
import {
	AxiosInstanceType,
	SlAxiosInstanceService,
} from '../private/services/sl-axios-instance.service';
import { SlApiContext } from '../private/api-context';
import { Service, Container } from '../../symphony';

@Service()
export class CommentsApi implements ICommentsApi {
	private environmentUrl: string;

	private codeCoachEnvUrl: string;

	private monolithApiHost: string;

	private useMock: boolean;

	private mock: ICommentsApiMock;

	private axiosInstance: AxiosInstanceType;

	constructor() {
		this.environmentUrl = (
            Container.take('global', 'envUrl') as IApiUrls
		).commentsApiHost;
		this.codeCoachEnvUrl = (
            Container.take('global', 'envUrl') as IApiUrls
		).codeCoachApiHost;
		this.monolithApiHost = (
            Container.take('global', 'envUrl') as IApiUrls
		).monolithApiHost;
		this.useMock = (
            Container.take('global', 'hostsConfigs') as IHostsConfigs
		)?.commentsApi?.useMock;
		this.mock = (
            Container.take('global', 'hostsConfigs') as IHostsConfigs
		)?.commentsApi?.mock;

		this.axiosInstance = Container.take(
			SlApiContext,
			SlAxiosInstanceService,
		).axios;
	}

    @Authorize(AuthLevel.authenticated)
	public getQuizComments(
		quizId: number,
		query: IQuizCommentQuery,
	): Observable<IQuizCommentResponse> {
		if (this.useMock) return of(this.mock.quizComments);
		return this.axiosInstance.get(
			`${this.environmentUrl}/api/quiz/${quizId}/comments`,
			{
				params: query,
			},
		);
	}

    @Authorize(AuthLevel.authenticated)
    public getLPComments(
    	commentContainerId: number,
    	query: ICommentQueryBase,
    ): Observable<ILPComment[]> {
    	return this.axiosInstance.get(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments`,
    		),
    		{
    			params: query,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public writeLPComment(
    	commentContainerId: number,
    	payload: ILPCommentBase,
    ): Observable<ILPComment> {
    	return this.axiosInstance.post(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments`,
    		),
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public editLPComment(
    	commentContainerId: number,
    	commentId: number,
    	payload: ILPCommentBase,
    ): Observable<ILPComment> {
    	return this.axiosInstance.put(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments/${commentId}`,
    		),
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public deleteLPComment(
    	commentContainerId: number,
    	commentId: number,
    ): Observable<null> {
    	return this.axiosInstance.delete(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments/${commentId}`,
    		),
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public voteLPComment(
    	commentContainerId: number,
    	commentId: number,
    	payload: ILPCommentVotePayload,
    ): Observable<CommentVoteState> {
    	return this.axiosInstance.post(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments/${commentId}/vote`,
    		),
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public getLPCommentsCount(commentContainerId: number): Observable<number> {
    	return this.axiosInstance.get(
    		constructUrl(
    			this.monolithApiHost,
    			`discussion/material/${commentContainerId}/comments/count`,
    		),
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public getCCComments(
    	query: ICCCommentQuery,
    ): Observable<ICCCommentsResponse> {
    	if (this.useMock) return of(this.mock.ccCommentsResponse);
    	return this.axiosInstance.get(`${this.codeCoachEnvUrl}/api/comment`, {
    		params: query,
    	});
    }

    @Authorize(AuthLevel.authenticated)
    public writeQuizComment(
    	quizId: number,
    	payload: IQuizCommentBase,
    ): Observable<IQuizCommentResponse> {
    	if (this.useMock) return of(this.mock.quizComments);
    	return this.axiosInstance.post(
    		`${this.environmentUrl}/api/quiz/${quizId}/comments`,
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public writeCCComment(
    	payload: ICCCommentBase,
    ): Observable<ICCCommentsResponse> {
    	if (this.useMock) return of(this.mock.ccCommentsResponse);
    	return this.axiosInstance.post(
    		`${this.codeCoachEnvUrl}/api/comment`,
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public deleteQuizComment(
    	quizId: number,
    	commentId: number,
    ): Observable<IQuizCommentResponse> {
    	if (this.useMock) return of(this.mock.quizComments);
    	return this.axiosInstance.delete(
    		`${this.environmentUrl}/api/quiz/${quizId}/comments/${commentId}`,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public deleteCCComment(
    	payload: IDeleteCCComment,
    ): Observable<ICCCommentsResponse> {
    	if (this.useMock) return of(this.mock.ccCommentsResponse);
    	return this.axiosInstance.delete(
    		`${this.codeCoachEnvUrl}/api/comment`,
    		{
    			data: payload,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public editQuizComment(
    	quizId: number,
    	commentId: number,
    	message: string,
    ): Observable<IQuizCommentResponse> {
    	if (this.useMock) return of(this.mock.quizComments);
    	return this.axiosInstance.put(
    		`${this.environmentUrl}/api/quiz/${quizId}/comments/${commentId}`,
    		{ message },
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public editCCComment(
    	payload: IEditCCCommentPayload,
    ): Observable<ICCCommentsResponse> {
    	if (this.useMock) return of(this.mock.ccCommentsResponse);
    	return this.axiosInstance.put(
    		`${this.codeCoachEnvUrl}/api/comment`,
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public voteQuizComment(
    	quizId: number,
    	commentId: number,
    	vote: CommentVoteState,
    ): Observable<IQuizCommentsVoteResponse> {
    	if (this.useMock) return of(this.mock.quizCommentsVote);
    	return this.axiosInstance.post(
    		`${this.environmentUrl}/api/quiz/${quizId}/comments/vote/${commentId}`,
    		{ vote },
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public voteCCComment(
    	payload: ICCCommentVotePayload,
    ): Observable<ICCCommentsVoteResponse> {
    	if (this.useMock) return of(this.mock.quizCommentsVote);
    	return this.axiosInstance.post(
    		`${this.codeCoachEnvUrl}/api/comment/vote`,
    		payload,
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public getQuizCommentsCount(
    	quizId: number,
    	query: { type: QuizCommentType },
    ): Observable<IQuizCommentsCountResponse> {
    	if (this.useMock) return of(this.mock.quizCommentsCount);
    	return this.axiosInstance.get(
    		`${this.environmentUrl}/api/quiz/${quizId}/comments/count`,
    		{
    			params: query,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public getCCCommentsCount(
    	ccId: number,
    ): Observable<ICCCommentsCountResponse> {
    	if (this.useMock) return of(this.mock.quizCommentsCount);
    	return this.axiosInstance.get(
    		`${this.codeCoachEnvUrl}/api/comment/count`,
    		{
    			params: {
    				problemId: ccId,
    			},
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public getLessonsCommentsCounts(
    	courseId: number,
    ): Observable<ILessonsCommentsCountsResponse> {
    	return this.axiosInstance.get(
    		constructUrl(this.environmentUrl, 'api/comments/lessons/counts'),
    		{
    			params: { courseId },
    		},
    	);
    }
}

export * from '../private/comments-api/comments-api.interface';
